import React, { useState, useEffect } from 'react';
import { Table, Tag, Card, Input, Select, Row, Col, Typography, message, Avatar, Divider, DatePicker, notification, Tooltip, Modal, Button, Space, Empty, Descriptions } from 'antd';
import SearchFieldsSelect, { useSearchFields, getSearchFieldsPlaceholder } from '../../Components/SearchFieldsSelect/SearchFieldsSelect';
import {
    ReloadOutlined,
    UserOutlined,
    CarOutlined,
    CalendarOutlined,
    GlobalOutlined,
    PhoneOutlined,
    MailOutlined,
    ClockCircleOutlined,
    SearchOutlined,
    CheckCircleOutlined,
    SyncOutlined,
    CommentOutlined,
    EyeOutlined,
    PhoneFilled,
    HistoryOutlined,
    FileTextOutlined,
    EditOutlined,
    CheckOutlined,
    CloseOutlined,
    
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API from '../../API/API';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import moment from 'moment';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MyFollowUps = () => {
    const navigate = useNavigate();
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchFields, setSearchFields] = useSearchFields('follow-ups:search-fields');
    // The "Search in" group only changes the results of a search, so changing it
    // with an empty search box sends no request.
    const effectiveSearchFields = searchTerm?.trim() ? searchFields : null;
   const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),  // First day of current month
    dayjs().endOf('month'),     // Last day of current month
]);
// Specification editing states
const [editingSpecification, setEditingSpecification] = useState(null);
const [editingSpecValue, setEditingSpecValue] = useState('');
const [updatingSpecification, setUpdatingSpecification] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [totalRecords, setTotalRecords] = useState(0);
    const [leadStage, setLeadStage] = useState('Follow-Up'); // default value
    const [leadType, setLeadType] = useState('All'); // default value
const [commentsModalVisible, setCommentsModalVisible] = useState(false);
const [selectedLeadForComment, setSelectedLeadForComment] = useState(null);
const [newComment, setNewComment] = useState('');
const [addingComment, setAddingComment] = useState(false);

const [datePreset, setDatePreset] = useState('thisMonth');

const [reFollowUpModalVisible, setReFollowUpModalVisible] = useState(false);
const [selectedLeadForReFollowUp, setSelectedLeadForReFollowUp] = useState(null);
const [newFollowUpDate, setNewFollowUpDate] = useState(null);
const [reFollowUpComment, setReFollowUpComment] = useState('');
const [updatingFollowUp, setUpdatingFollowUp] = useState(false);
const [historyModalVisible, setHistoryModalVisible] = useState(false);
const [selectedLeadForHistory, setSelectedLeadForHistory] = useState(null);

const formatPSTDate = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).tz('America/Los_Angeles').format('MM-DD-YYYY');
};

const formatPSTTime = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).tz('America/Los_Angeles').format('hh:mm A');
};

const formatPSTDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return dayjs(dateString).tz('America/Los_Angeles').format('MM-DD-YYYY hh:mm A');
};

const getFollowUpActionTime = (item) => {
    const dateValue = item?.createdAt || item?.completedDate || item?.scheduledDate;
    const timestamp = dateValue ? new Date(dateValue).getTime() : 0;
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getSortedFollowUpHistory = (lead) => (
    [...(lead?.followUpHistory || [])]
        .map((item, index) => ({ ...item, originalIndex: index }))
        .sort((a, b) => (getFollowUpActionTime(b) - getFollowUpActionTime(a)) || (b.originalIndex - a.originalIndex))
);

const getHandledByName = (handledBy) => {
    if (!handledBy || typeof handledBy !== 'object') return 'Unknown User';

    const firstLastName = `${handledBy.firstName || ''} ${handledBy.lastName || ''}`.trim();
    const name = handledBy.fullName
        || handledBy.name
        || firstLastName
        || handledBy.username
        || handledBy.userName
        || handledBy.email;

    if (!name) return 'Unknown User';
    return handledBy.role ? `${handledBy.role} - ${name}` : name;
};

const calculateFollowUpStatus = (followUpDate) => {
    if (!followUpDate) return null;

    const followDate = dayjs(followUpDate);
    const now = dayjs();

    const totalHours = followDate.diff(now, 'hour', true);
    const totalMinutes = followDate.diff(now, 'minute');

    let boxBgColor = "#f0f9ff";
    let boxBorderColor = "#d0e7ff";
    let statusText = "";
    let tagColor = "success";
    let icon = "⏰";

    if (followDate.isBefore(now)) {
        boxBgColor = "#fff2f0";
        boxBorderColor = "#ffccc7";
        statusText = "Missed";
        tagColor = "error";
        icon = "⚠️";
    } else if (totalHours <= 8) {
        boxBgColor = "#f6ffed";
        boxBorderColor = "#b7eb8f";
        tagColor = "success";
        icon = "⏰";
    } else {
        boxBgColor = "#fff7e6";
        boxBorderColor = "#ffd591";
        tagColor = "warning";
        icon = "⏰";
    }

    if (!followDate.isBefore(now)) {
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;

        let parts = [];

        if (days > 0) parts.push(`${days}d`);
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);

        statusText = `In ${parts.join(" ")}`;
    }

    return {
        boxBgColor,
        boxBorderColor,
        statusText,
        tagColor,
        icon,
        isPast: followDate.isBefore(now)
    };
};

const handleReFollowUp = async () => {
    if (!newFollowUpDate) {
        message.warning('Please select a follow-up date and time');
        return;
    }

    if (!selectedLeadForReFollowUp) return;

    setUpdatingFollowUp(true);
    try {
        const selectedDate = dayjs(newFollowUpDate).tz('America/Los_Angeles', true);
        const nowPST = dayjs().tz('America/Los_Angeles');
        
        if (selectedDate.isBefore(nowPST)) {
            message.error('Follow-up date/time cannot be in the past');
            setUpdatingFollowUp(false);
            return;
        }

        // Prepare payload
        const payload = {
            followUpDate: selectedDate.toISOString(),
            leadStage: "Follow-Up" // Automatically set stage to Follow-Up
        };

        // Add comment if provided
        if (reFollowUpComment.trim()) {
            payload.comment = reFollowUpComment.trim();
            
            // Also add to followUpPageComment history
            const existingComments = selectedLeadForReFollowUp.followUpPageComment || [];
            payload.followUpPageComment = [
                ...existingComments,
                {
                    comment: `Re-FollowUp scheduled: ${reFollowUpComment.trim()}`,
                    changedAt: new Date().toISOString()
                }
            ];
        }

        const response = await API.UpdateLead(selectedLeadForReFollowUp._id, payload);

        if (response?.status) {
            message.success('Re-follow-up scheduled successfully!');
            setReFollowUpModalVisible(false);
            setSelectedLeadForReFollowUp(null);
            setNewFollowUpDate(null);
            setReFollowUpComment('');
            // Refresh the leads list
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to schedule re-follow-up');
        }
    } catch (error) {
        console.error('Error scheduling re-follow-up:', error);
        message.error('Failed to schedule re-follow-up');
    } finally {
        setUpdatingFollowUp(false);
    }
};
const handleUpdateSpecification = async (leadId, newSpecification) => {
    if (!leadId) return;
    
    const trimmedValue = newSpecification?.trim() || '';
    
    if (trimmedValue.length > 2000) {
        message.warning('Specification cannot exceed 2000 characters');
        return;
    }
    
    setUpdatingSpecification(true);
    try {
        // First, get the current lead data to preserve other vehicleInfo fields
        const currentLead = leads.find(lead => lead._id === leadId);
        const currentVehicleInfo = currentLead?.vehicleInfo || {};
        
        // Merge the specification with existing vehicleInfo
        const payload = {
            vehicleInfo: {
                ...currentVehicleInfo, // Preserve all existing fields
                specification: trimmedValue // Update only specification
            }
        };
        
        console.log('Sending payload with preserved data:', payload);
        
        const response = await API.UpdateLead(leadId, payload);
        
        if (response?.status) {
            message.success('Specification updated successfully!');
            fetchLeads(); // Refresh the leads list
        } else {
            message.error(response?.message || 'Failed to update specification');
        }
    } catch (error) {
        console.error('Error updating specification:', error);
        message.error('Failed to update specification');
    } finally {
        setUpdatingSpecification(false);
        setEditingSpecification(null);
        setEditingSpecValue('');
    }
};
const handleAddComment = async () => {
    if (!newComment.trim()) {
        message.warning('Please enter a comment');
        return;
    }

    if (!selectedLeadForComment) return;

    setAddingComment(true);
    try {
        const existingComments = selectedLeadForComment.followUpPageComment || [];
        const payload = {
            followUpPageComment: [
                ...existingComments,
                {
                    comment: newComment.trim(),
                    changedAt: new Date().toISOString()
                }
            ]
        };

        const response = await API.UpdateLead(selectedLeadForComment._id, payload);

        if (response?.status) {
            message.success('Comment added successfully!');
            setNewComment('');
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            // Refresh the leads list
            fetchLeads();
        } else {
            message.error(response?.message || 'Failed to add comment');
        }
    } catch (error) {
        console.error('Error adding comment:', error);
        message.error('Failed to add comment');
    } finally {
        setAddingComment(false);
    }
};

    const fetchLeads = async () => {
        setLoading(true);

        try {
            const userData = JSON.parse(localStorage.getItem("userData"));

            if (!userData?.id) {
                console.error("User authentication data not found");
                setLoading(false);
                return;
            }

            const effectiveDateRange =
                Array.isArray(dateRange) && dateRange.length === 2 && dateRange[0] && dateRange[1]
                    ? dateRange
                    : [dayjs().startOf('month'), dayjs().endOf('month')];

            if (effectiveDateRange !== dateRange) {
                setDateRange(effectiveDateRange);
                setDatePreset('thisMonth');
            }

            const params = {
                 dateField: 'followUpDate',
                startDate: effectiveDateRange[0].format('YYYY-MM-DD'),
                endDate: effectiveDateRange[1].format('YYYY-MM-DD'),
                page: currentPage,
                limit: pageSize,
                isFollowUpPage: true,
            };

            if (searchTerm?.trim()) {
                params.search = searchTerm.trim();
                params.searchFields = searchFields;
            }
            if (leadStage && leadStage !== "All") params.status = leadStage;
            if (leadType && leadType !== "All") params.type = leadType;
            if (userData?.role !== 'ADMIN') params.agentId = userData.id;

            // The server sends only the fields this page uses
            // (LeadController LEAD_VIEW_FIELDS['follow-ups']).
            params.view = 'follow-ups';
            const response = await API.leadGetAll(params);

            if (response?.status) {
                let filteredData = response.data;

                if (leadStage === "All") {
                    filteredData = filteredData.filter(lead => lead.leadStage !== "New");
                }

                setLeads(filteredData);
                setTotalRecords(response.pagination.totalRecords);
            } else {
                console.error("Invalid response format:", response);
                setLeads([]);
            }

        } catch (error) {
            console.error('Failed to fetch leads:', error);
            setLeads([]);
        } finally {
            setLoading(false);
        }
    };

const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const now = dayjs();
    let startDate, endDate;

    switch (preset) {
        case 'today':
            startDate = now.startOf('day');
            endDate = now.endOf('day');
            break;
        case 'yesterday':
            startDate = now.subtract(1, 'day').startOf('day');
            endDate = now.subtract(1, 'day').endOf('day');
            break;
        case 'thisWeek':
            startDate = now.startOf('week');
            endDate = now.endOf('day');
            break;
        case 'thisMonth':
            startDate = now.startOf('month');
            endDate = now.endOf('month');
            break;
        case 'lastMonth':
            startDate = now.subtract(1, 'month').startOf('month');
            endDate = now.subtract(1, 'month').endOf('month');
            break;
       
        case 'thisYear':
            startDate = now.startOf('year');
            endDate = now.endOf('year');
            break;
        case 'custom':
            // Keep existing custom range
            return;
        default:
            startDate = now.startOf('month');
            endDate = now.endOf('month');
    }

    setDateRange([startDate, endDate]);
};

useEffect(() => {
    const delaySearch = setTimeout(() => {
        fetchLeads();
    }, 500);

    return () => clearTimeout(delaySearch);
}, [currentPage, pageSize, dateRange, searchTerm, effectiveSearchFields, leadStage, leadType, datePreset]); 

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    const leadStages = [
        { text: 'New', value: 'New' },

        { text: 'Call Back', value: 'Call Back' },
        { text: 'Voice Mail', value: 'Voice Mail' },
        { text: 'Follow-Up', value: 'Follow-Up' },
        { text: 'Quoted', value: 'Quoted' },
        { text: 'Pending Payment', value: 'Pending Payment' },
        { text: 'Payment Completed', value: 'Payment Completed' },
        { text: 'Sales Made', value: 'Sales Made' },
        { text: 'Part Not Available', value: 'Part Not Available' },
        { text: 'Wrong Number', value: 'Wrong Number' },
        { text: 'Not Interested', value: 'Not Interested' },
        { text: 'Hang Up', value: 'Hang Up' },
        { text: 'Fake', value: 'Fake' },
        { text: 'Test Lead', value: 'Test Lead' },
        { text: 'Negotiation', value: 'Negotiation' },
    ];



    const leadStageColors = {
        'New': { color: '#1890ff', background: '#e6f7ff' }, // Cool blue, fresh lead
        'Call Back': { color: '#faad14', background: '#fffbe6' }, // Amber for pending action
        'Voice Mail': { color: '#9254de', background: '#f9f0ff' }, // Light purple, neutral waiting
        'Follow-Up': { color: '#fa8c16', background: '#fff7e6' }, // Orange, action needed
        'Quoted': { color: '#13c2c2', background: '#e6fffb' }, // Teal, info shared
        'Negotiation': { color: '#eb2f96', background: '#fff0f6' }, // Pink, active dialogue
        'Pending Payment': { color: '#fa541c', background: '#fff2e8' }, // Bold orange-red, urgency
        'Payment Completed': { color: '#52c41a', background: '#f6ffed' }, // Green, success
        'Sales Made': { color: '#389e0d', background: '#f6ffed' }, // Deeper green, closed deal
        'Part Not Available': { color: '#bfbfbf', background: '#fafafa' }, // Neutral gray
        'Wrong Number': { color: '#ff4d4f', background: '#fff1f0' }, // Red, error
        'Not Interested': { color: '#d46b08', background: '#fff7e6' }, // Muted orange, closed
        'Hang Up': { color: '#8c8c8c', background: '#f5f5f5' }, // Dark gray, dead
        'Fake': { color: '#a8071a', background: '#fff1f0' }, // Deep red, invalid
        'Test Lead': { color: '#7cb305', background: '#fcffe6' }, // Lime, internal
    };


    const getStageColor = (stage) => {
        return leadStageColors[stage] || { color: '#000', background: '#fff' }; // Fallback for unknown stages
    };

    

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    };

    const getAvatarColor = (name) => {
        if (!name) return '#1890ff';
        const colors = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    };

    const getTimeAgo = (followUpDate) => {
    if (!followUpDate) return { label: 'N/A', priority: 'N/A' };

    const followDate = new Date(followUpDate);
    const now = new Date();

    // Normalize to midnight for accurate day comparison
    followDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);

    const diffTime = followDate - now;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // Time label
    let label;
    if (diffDays === 0) {
        label = 'TODAY';
    } else if (diffDays === 1) {
        label = 'Tomorrow';
    } else if (diffDays > 1) {
        label = `${diffDays} days to go`;
    } else if (diffDays === -1) {
        label = 'Yesterday';
    } else {
        label = `${Math.abs(diffDays)} days ago`;
    }

    // Priority - Order: Overdue (highest) → High → Medium → Low (lowest)
    let priority;
    let priorityOrder = 0; // For sorting if needed
    
    if (diffDays < 0) {
        priority = 'Overdue';
        priorityOrder = 1; // Highest priority
    } else if (diffDays === 0) {
        priority = 'High Priority';
        priorityOrder = 2;
    } else if (diffDays <= 3) {
        priority = 'Medium Priority';
        priorityOrder = 3;
    } else {
        priority = 'Low Priority';
        priorityOrder = 4;
    }

    return { label, priority, priorityOrder };
};
const formatCommentDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit"
    });
};

    const columns = [
        ...(JSON.parse(localStorage.getItem("userData")).role === "ADMIN" ? [{ title: "Sales-Agent", width: 150, dataIndex: "agentName", key: "agentName" }] : []),
        {
            title: 'Customer',
            dataIndex: 'personalInfo',
            key: 'personalInfo',
            render: (personalInfo, record) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar
                        style={{
                            backgroundColor: getAvatarColor(personalInfo.fullName),
                            marginRight: '12px',
                        }}
                        size={40}
                    >
                        {getInitials(personalInfo.fullName)}
                    </Avatar>
                    <div>
                        <Text strong style={{ display: 'block', fontSize: '15px' }}>{personalInfo.fullName}</Text>
                        <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
                            <PhoneOutlined style={{ marginRight: '5px' }} />
                            <Text type="" >{personalInfo.phoneNumber}</Text>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', color: '#8c8c8c', fontSize: '12px', marginTop: '4px' }}>
                            <MailOutlined style={{ marginRight: '5px' }} />
                            <Text type="" >{personalInfo.email}</Text>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Vehicle',
            dataIndex: 'vehicleInfo',
            key: 'vehicleInfo',
            render: (vehicleInfo) => (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
                        <CarOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                        <Text>{vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}</Text><br />
                    </div>
                    <Text type="" style={{ marginLeft: '24px' }}>
                        Part: {vehicleInfo.part}
                    </Text><br />
                    <Text type="" style={{ marginLeft: '24px' }}>{vehicleInfo.vin}</Text>

                </div>
            ),
        },

       {
    title: 'Specification',
    key: 'specification',
    width: 250,
    render: (_, record) => {
        const specification = record.vehicleInfo?.specification || '';
        const isEditing = editingSpecification === record._id;
        
        if (isEditing) {
            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <Input.TextArea
                        autoSize={{ minRows: 2, maxRows: 4 }}
                        value={editingSpecValue}
                        onChange={(e) => setEditingSpecValue(e.target.value)}
                        placeholder="Enter specification details..."
                        style={{ flex: 1, fontSize: '12px' }}
                        autoFocus
                        maxLength={2000}
                        showCount
                    />
                    <div style={{ display: 'flex', gap: '4px', flexDirection: 'column' }}>
                        <Button
                            size="small"
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={updatingSpecification}
                            onClick={() => handleUpdateSpecification(record._id, editingSpecValue)}
                            style={{ minWidth: '32px' }}
                        />
                        <Button
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setEditingSpecification(null);
                                setEditingSpecValue('');
                            }}
                        />
                    </div>
                </div>
            );
        }
        
        return (
            <div 
                style={{ 
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '6px',
                    transition: 'all 0.3s',
                    backgroundColor: specification ? 'transparent' : '#fafafa',
                    border: '1px solid transparent',
                    minHeight: '60px'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#d9d9d9';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = specification ? 'transparent' : '#fafafa';
                    e.currentTarget.style.borderColor = 'transparent';
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    setEditingSpecification(record._id);
                    setEditingSpecValue(specification);
                }}
            >
                {specification ? (
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <FileTextOutlined style={{ color: '#52c41a', fontSize: '12px' }} />
                            <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>Specification:</Text>
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            whiteSpace: 'pre-wrap', 
                            wordBreak: 'break-word',
                            display: 'block',
                            lineHeight: '1.5'
                        }}>
                            {specification.length > 100 
                                ? `${specification.substring(0, 100)}...` 
                                : specification}
                        </Text>
                        <div style={{ marginTop: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <EditOutlined style={{ marginRight: '4px' }} />
                                Click to edit
                            </Text>
                        </div>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '12px' }}>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <FileTextOutlined style={{ marginRight: '4px', color: '#52c41a' }} />
                            <EditOutlined style={{ marginRight: '4px', fontSize: '10px' }} />
                            Click to add specification
                        </Text>
                    </div>
                )}
            </div>
        );
    },
}, 
 {
    title: 'Follow Up',
    key: 'followUpDate',
    width: 280,
    render: (_, record) => {
        const status = calculateFollowUpStatus(record.followUpDate);

        return (
            <div>
                {record.followUpDate ? (
                    <div
                        style={{
                            backgroundColor: status?.boxBgColor,
                            border: `1px solid ${status?.boxBorderColor}`,
                            borderRadius: '10px',
                            padding: '12px',
                        }}
                    >
                        {/* Date Row */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: '10px',
                            }}
                        >
                            {/* Date */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <CalendarOutlined
                                    style={{
                                        marginRight: '8px',
                                        color: '#1890ff',
                                        fontSize: '14px',
                                    }}
                                />

                                <Text strong style={{ fontSize: '13px' }}>
                                    {formatPSTDate(record.followUpDate)}
                                </Text>
                            </div>

                            {/* Time */}
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <ClockCircleOutlined
                                    style={{
                                        marginRight: '6px',
                                        fontSize: '12px',
                                        color: '#8c8c8c',
                                    }}
                                />

                                <Text
                                    type="secondary"
                                    style={{ fontSize: '12px' }}
                                >
                                    {formatPSTTime(record.followUpDate)} PST
                                </Text>
                            </div>
                        </div>

                        {/* Bottom Row */}
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            {/* Status */}
                            <Tag
                                color={status?.tagColor}
                                style={{
                                    margin: 0,
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    borderRadius: '20px',
                                    padding: '2px 10px',
                                }}
                            >
                                {status?.icon} {status?.statusText}
                            </Tag>

                            {/* Button */}
                            <Space size={6}>
                                <Tooltip title="View Follow-Up History">
                                    <Button
                                        size="small"
                                        icon={<HistoryOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedLeadForHistory(record);
                                            setHistoryModalVisible(true);
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="Schedule Re-FollowUp">
                                    <Button
                                        size="small"
                                        type="primary"
                                        style={{
                                            background: '#52c41a',
                                            borderColor: '#52c41a',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            height: '30px',
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();

                                            setSelectedLeadForReFollowUp(record);
                                            setNewFollowUpDate(null);
                                            setReFollowUpComment('');
                                            setReFollowUpModalVisible(true);
                                        }}
                                    >
                                        Re-Schedule
                                    </Button>
                                </Tooltip>
                            </Space>
                        </div>
                    </div>
                ) : (
                    <Text type="secondary">No Follow up</Text>
                )}
            </div>
        );
    },
},
 
{
    title: 'Comment',
    key: 'followUpPageComment',
    width: 280,
    render: (_, record) => {
        const comments = record.followUpPageComment || [];
        const latestComment = comments.length > 0 ? comments[comments.length - 1] : null;
        
        return (
            <div>
             

                {/* Display Latest Comment */}
                {latestComment ? (
                    <div 
                        style={{
                            background: '#fafafa',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            wordBreak: 'break-word',      // Add this
                            wordWrap: 'break-word',       // Add this
                            overflowWrap: 'break-word',   // Add this
                            whiteSpace: 'normal'          // Add this
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <Text type="secondary" style={{ fontSize: '10px' }}>
                                <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                {formatCommentDate(latestComment.changedAt)}
                            </Text>
                            {comments.length > 1 && (
                                <Tag 
                                    color="blue" 
                                    style={{ 
                                        fontSize: '10px', 
                                        padding: '0 4px', 
                                        margin: 0,
                                        cursor: 'pointer'
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedLeadForComment(record);
                                        setCommentsModalVisible(true);
                                    }}
                                >
                                    +{comments.length - 1} more
                                </Tag>
                            )}
                        </div>
                        <Text style={{ 
                            fontSize: '12px', 
                            display: 'block', 
                            wordBreak: 'break-word',    // Add this
                            whiteSpace: 'pre-wrap'       // Add this for preserving whitespace and wrapping
                        }}>
                            {latestComment.comment.length > 80 
                                ? `${latestComment.comment.substring(0, 80)}...` 
                                : latestComment.comment}
                        </Text>
                    </div>
                ) : (
                    <div 
                        style={{ 
                            textAlign: 'center', 
                            padding: '12px',
                            background: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #f0f0f0',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSelectedLeadForComment(record);
                            setCommentsModalVisible(true);
                        }}
                    >
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                            <CommentOutlined style={{ marginRight: '4px', fontSize: '14px' }} />
                            No comments yet
                        </Text>
                    </div>
                )}
                   <Button
                    type="primary"
                    size="small"
                    icon={<CommentOutlined />}
                    onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLeadForComment(record);
                        setNewComment('');
                        setCommentsModalVisible(true);
                    }}
                    style={{ 
                        marginBottom: '12px',
                        width: '100%',
                        fontSize: '12px'
                    }}
                >
                    Add Comment
                </Button>
            </div>
        );
    },
},
 
  {
    title: 'Date Info',
    key: 'dateInfo',
    width: 200,
    sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    render: (_, record) => (
        <Tooltip
            title={
                <>
                    <div><strong>Created:</strong> {record.createdAt ? dayjs(record.createdAt).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                    <div><strong>Updated:</strong> {record.updatedAt ? dayjs(record.updatedAt).format('MM-DD-YYYY hh:mm A') : 'N/A'}</div>
                </>
            }
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

                {/* Created */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#F1EFE8',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <ClockCircleOutlined style={{ fontSize: 13, color: '#888780' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>
                            {record.createdAt ? dayjs(record.createdAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Created · {record.createdAt ? dayjs(record.createdAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

                {/* Updated */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                        width: 28, height: 28, borderRadius: 8,
                        background: '#E6F1FB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <SyncOutlined style={{ fontSize: 13, color: '#185FA5' }} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Text style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2, color: '#185FA5' }}>
                            {record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YY · hh:mm A') : 'N/A'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.2 }}>
                            Updated · {record.updatedAt ? dayjs(record.updatedAt).fromNow() : '—'}
                        </Text>
                    </div>
                </div>

            </div>
        </Tooltip>
    ),
},
{
    title: 'Action',
    key: 'action',
    width: 180, // Increased width to accommodate new button
    fixed: 'right',
    render: (_, record) => (
        <div
            style={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
        >
            <Tooltip title={record.personalInfo?.phoneNumber ? "Call Customer" : "No phone number"}>
                <Button
                    icon={<PhoneOutlined />}
                    href={`tel:${record.personalInfo?.phoneNumber}`}
                    disabled={!record.personalInfo?.phoneNumber}
                    size="small"
                    shape="circle"
                    type="primary"
                    ghost
                    onClick={() => {
                        // setCalledLeads(prev => new Set(prev).add(record._id));
                    }}
                />
            </Tooltip>
            
            {/* NEW: Re-FollowUp Button */}
          
            
            <Tooltip title="View Details">
                <Button
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/lead-details/${record._id}`)}
                    size="small"
                    shape="circle"
                    type="primary"
                    style={{ 
                        background: `linear-gradient(135deg, #1890ff 0%, #40a9ff 100%)`,
                        border: 'none'
                    }}
                />
            </Tooltip>
        </div>
    ),
},

    ];

    const getLeadPriority = (record) => {
        const daysAgo = new Date() - new Date(record.createdAt || record.orderDate);
        const daysDiff = Math.floor(daysAgo / (1000 * 60 * 60 * 24));

        if (record.leadStage === 'Follow-Up' && daysDiff > 7) return 'high';
        if (record.leadStage === 'Negotiation') return 'medium';
        return 'low';
    };

    const leadTypeColors = {
        'inbound': { color: '#52c41a', background: '#f6ffed' },
        'outbound': { color: '#1890ff', background: '#e6f7ff' },
    };

    return (
        <div >

            {/* <Card
                bordered={false}
                style={{
                    borderRadius: '12px',
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03), 0 2px 4px rgba(0, 0, 0, 0.03)'
                }}
            > */}
            {/* Header and Title */}
            <Row gutter={[16, 16]} align="middle">
                <Col span={24}>
                    <Title level={4} style={{ margin: 0 }}>My Leads Tracking</Title>
                    <Text type="secondary">
                        Manage and track your customer
                    </Text>
                </Col>
            </Row>

            <Divider style={{ margin: '16px 0' }} />

            {/* Filter Section - Always Visible */}
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} md={6}>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Search</Text>
                    <Space.Compact block>
                        <SearchFieldsSelect value={searchFields} onChange={setSearchFields} size="middle" />
                        <Input.Search
                            placeholder={getSearchFieldsPlaceholder(searchFields)}
                            value={searchTerm}
                            onChange={(e) => {
                                console.log("Search Input Changed:", e.target.value); // ✅ Debug log
                                setSearchTerm(e.target.value);
                            }}
                            allowClear
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        />
                    </Space.Compact>
                </Col>
                <Col xs={12} md={4}>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Lead Stage</Text>
                    <Select
                        value={leadStage}
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            setLeadStage(value);
                            setCurrentPage(1); // Reset to first page when filtering
                        }}
                    >
                        {leadStages.map((stage) => (
                            <Option key={stage.value} value={stage.value}>
                                {stage.text}
                            </Option>
                        ))}
                    </Select>

                </Col>
                <Col xs={12} md={4}>
                    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Lead Type</Text>

                    <Select
                        style={{ width: '100%' }}
                        value={leadType}
                        onChange={(value) => {
                            setLeadType(value);
                            setCurrentPage(1); // Reset to first page when filtering
                        }}
                    >
                        <Option value="All">All Types</Option>
                        <Option value="inbound">Inbound</Option>
                        <Option value="outbound">Outbound</Option>
                    </Select>
                </Col>
              <Col xs={24} md={6}>
    <Text strong style={{ display: 'block', marginBottom: '8px' }}>Date Range</Text>
    
    {/* Add the preset selector dropdown */}
    <Select
        value={datePreset}
        onChange={handleDatePresetChange}
        style={{ width: '100%', marginBottom: '8px' }}
        size="middle"
    >
        <Option value="today">Today</Option>
        <Option value="yesterday">Yesterday</Option>
        <Option value="thisWeek">This Week</Option>
        <Option value="thisMonth">This Month</Option>
        <Option value="lastMonth">Last Month</Option>
        <Option value="thisYear">This Year</Option>
        <Option value="custom">Custom Range</Option>
    </Select>
    
   {datePreset === 'custom' && (
  <RangePicker 
        value={dateRange} 
        onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
                setDatePreset('custom');
                setDateRange(dates);
            } else {
                setDatePreset('thisMonth');
                setDateRange([dayjs().startOf('month'), dayjs().endOf('month')]);
            }
        }}
        style={{ width: '100%' }}
       
    />
   )}
  
</Col>
                <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button
                        style={{
                            width: '100%',
                            padding: '8px 0',
                            border: 'none',
                            borderRadius: '6px',
                            backgroundColor: '#1890ff',
                            color: 'white',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '32px'
                        }}
                        onClick={fetchLeads}
                    >
                        <ReloadOutlined style={{ marginRight: '8px' }} /> Refresh
                    </button>
                </Col>
            </Row>

            {/* Table */}
            <Table
                scroll={{
                    x: 'max-content',
                    y: '40vh'
                }}
                columns={columns}
                dataSource={leads}
                rowKey={(record) => record._id || record.key}
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: totalRecords,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                    showTotal: (total, range) => `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                    position: ['topRight'],
                }}
                loading={loading}
                onChange={handleTableChange}
               
                rowClassName={(record) => `lead-table-row ${getLeadPriority(record)}-priority-row`}

            />

{/* Add Comment Modal */}
<Modal
   
    open={commentsModalVisible}
    onCancel={() => {
        setCommentsModalVisible(false);
        setSelectedLeadForComment(null);
        setNewComment('');
    }}
    footer={[
        <Button key="cancel" onClick={() => {
            setCommentsModalVisible(false);
            setSelectedLeadForComment(null);
            setNewComment('');
        }}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            onClick={handleAddComment}
            loading={addingComment}
            disabled={!newComment.trim()}
        >
            Add Comment
        </Button>
    ]}
    width={500}
>
    <div style={{ padding: '16px 0' }}>
      

        <div>
            <Text strong>Comment:</Text>
            <Input.TextArea
                placeholder="Enter your comment here..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={4}
                maxLength={1000}
                showCount
                style={{ marginTop: '8px' }}
                autoFocus
            />
        </div>

        {/* Show existing comments if any */}
        {selectedLeadForComment?.followUpPageComment?.length > 0 && (
            <div style={{ marginTop: '20px' }}>
                <Divider />
                <Text strong>Previous Comments ({selectedLeadForComment.followUpPageComment.length})</Text>
                <div style={{ 
                    maxHeight: '200px', 
                    overflowY: 'auto', 
                    marginTop: '8px',
                    padding: '8px',
                    background: '#f5f5f5',
                    borderRadius: '8px'
                }}>
                    {[...selectedLeadForComment.followUpPageComment]
                        .sort((a, b) => new Date(b.changedAt) - new Date(a.changedAt))
                        .map((comment, idx) => (
                            <div key={idx} style={{ 
                                marginBottom: '12px', 
                                padding: '8px',
                                background: 'white',
                                borderRadius: '6px',
                                border: '1px solid #e8e8e8'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                        {formatCommentDate(comment.changedAt)}
                                    </Text>
                                </div>
                                <Text style={{ fontSize: '13px' }}>{comment.comment}</Text>
                            </div>
                        ))}
                </div>
            </div>
        )}
    </div>
</Modal>
{/* Follow-Up History Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: '#1890ff' }} />
            <span>Follow-Up History</span>
        </div>
    }
    open={historyModalVisible}
    onCancel={() => {
        setHistoryModalVisible(false);
        setSelectedLeadForHistory(null);
    }}
    footer={[
        <Button key="close" onClick={() => {
            setHistoryModalVisible(false);
            setSelectedLeadForHistory(null);
        }}>
            Close
        </Button>
    ]}
    width={720}
>
    {(() => {
        const history = getSortedFollowUpHistory(selectedLeadForHistory);

        return (
            <div style={{ padding: '8px 0' }}>
                {selectedLeadForHistory && (
                    <div style={{
                        marginBottom: 16,
                        padding: 12,
                        background: '#f5f5f5',
                        borderRadius: 8
                    }}>
                        <Text strong>Latest Follow-Up: </Text>
                        <Text>
                            {selectedLeadForHistory.followUpDate
                                ? `${formatPSTDate(selectedLeadForHistory.followUpDate)} ${formatPSTTime(selectedLeadForHistory.followUpDate)} PST`
                                : 'N/A'}
                        </Text>
                    </div>
                )}

                {history.length === 0 ? (
                    <Empty description="No follow-up history yet" />
                ) : (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                        {history.map((item, index) => (
                            <Card
                                key={item._id || `${item.scheduledDate}-${index}`}
                                size="small"
                                title={`Follow-Up #${history.length - index}`}
                                extra={<Tag color={item.status === 'Completed' ? 'green' : 'blue'}>{item.status || 'Scheduled'}</Tag>}
                            >
                                <Descriptions column={1} size="small" colon>
                                    <Descriptions.Item label="Follow-Up Date">
                                        {formatPSTDate(item.scheduledDate)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Follow-Up Time">
                                        {formatPSTTime(item.scheduledDate)} PST
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Changed By / Scheduled By">
                                        {getHandledByName(item.handledBy)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Comment / Note">
                                        {item.notes || 'No note'}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Created/Changed At">
                                        {formatPSTDateTime(item.createdAt || item.completedDate || item.scheduledDate)} PST
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        ))}
                    </Space>
                )}
            </div>
        );
    })()}
</Modal>
{/* Re-FollowUp Modal */}
<Modal
    title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <SyncOutlined style={{ color: '#52c41a' }} />
            <span>Schedule Re-FollowUp</span>
        </div>
    }
    open={reFollowUpModalVisible}
    onCancel={() => {
        setReFollowUpModalVisible(false);
        setSelectedLeadForReFollowUp(null);
        setNewFollowUpDate(null);
        setReFollowUpComment('');
    }}
    footer={[
        <Button key="cancel" onClick={() => {
            setReFollowUpModalVisible(false);
            setSelectedLeadForReFollowUp(null);
            setNewFollowUpDate(null);
            setReFollowUpComment('');
        }}>
            Cancel
        </Button>,
        <Button 
            key="submit" 
            type="primary" 
            onClick={handleReFollowUp}
            loading={updatingFollowUp}
            disabled={!newFollowUpDate}
            icon={<SyncOutlined />}
            style={{ background: '#52c41a' }}
        >
            Schedule Re-FollowUp
        </Button>
    ]}
    width={500}
>
    <div style={{ padding: '16px 0' }}>
        {/* Customer Info */}
        {selectedLeadForReFollowUp && (
            <div style={{ 
                marginBottom: 20, 
                padding: 12, 
                background: '#f5f5f5', 
                borderRadius: 8 
            }}>
                <Text strong>Customer: </Text>
                <Text>{selectedLeadForReFollowUp.personalInfo?.fullName}</Text>
                <br />
                <Text strong>Phone: </Text>
                <Text>{selectedLeadForReFollowUp.personalInfo?.phoneNumber}</Text>
                <br />
                <Text strong>Vehicle: </Text>
                <Text>
                    {selectedLeadForReFollowUp.vehicleInfo?.year} {selectedLeadForReFollowUp.vehicleInfo?.make} {selectedLeadForReFollowUp.vehicleInfo?.model}
                </Text>
            </div>
        )}

        {/* Follow-Up Date Picker */}
        <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                <CalendarOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                New Follow-Up Date & Time <span style={{ color: 'red' }}>*</span>
            </Text>
            <DatePicker
                style={{ width: '100%' }}
                value={newFollowUpDate}
                onChange={(date) => setNewFollowUpDate(date)}
                showTime={{ format: 'hh:mm A', use12Hours: true, defaultOpenValue: dayjs().tz('America/Los_Angeles') }}
                format="MMM DD, YYYY hh:mm A"
                placeholder="Select follow-up date and time"
                disabledDate={(current) => {
                    const todayPST = dayjs().tz('America/Los_Angeles').startOf('day');
                    return current && current.startOf('day').isBefore(todayPST);
                }}
                size="large"
            />
        </div>

        {/* Comment Field */}
        <div style={{ marginTop: 16 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
                <CommentOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                Comment (Optional)
            </Text>
            <Input.TextArea
                placeholder="Enter reason for re-follow-up or any additional notes..."
                value={reFollowUpComment}
                onChange={(e) => setReFollowUpComment(e.target.value)}
                rows={4}
                maxLength={500}
                showCount
            />
        </div>

        {/* Info Message */}
        <div style={{ 
            marginTop: 16, 
            padding: 8, 
            background: '#e6f7ff', 
            borderRadius: 6,
            border: '1px solid #91d5ff'
        }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
                <SyncOutlined style={{ marginRight: 8 }} />
                This will update the follow-up date and automatically change the lead stage to "Follow-Up".
            </Text>
        </div>
    </div>
</Modal>
        </div>
    );
};

export default MyFollowUps;

